import React, { useEffect, useState } from "react";

import { addHours, set } from "date-fns";

import { createFakeEvent, formatCurrency } from "@ds-developer/frontend";
import { fireOnce } from "@ds-developer/frontend";
import { useTealium } from "@hubcapp/context";

import {
  AllowedRepeatingLesson,
  AllowedRepeatingLessonFrequencyNumber,
  AllowedTravelTime,
  LessonFormFieldsStateProviderProps,
} from "../../types";
import { Learner } from "@hubcapp/types";
import LessonFormFields from "./LessonFormFields";

const StateProvider: React.FC<LessonFormFieldsStateProviderProps> = ({
  learnersLoading,
  learners,
  values,
  handleChange,
}) => {
  const [startDate, setStartDate] = useState<Date | null | undefined>(
    values.startDateTime || null,
  );
  const [startTime, setStartTime] = useState<Date | null | undefined>(
    values.startDateTime || null,
  );
  const [endTime, setEndTime] = useState<Date | null | undefined>(
    values.endDateTime || null,
  );

  const [travelTime, setTravelTime] = useState<AllowedTravelTime>(
    values.travelTime || 0,
  );

  const [repeatingLesson, setRepeatingLesson] = useState<
    AllowedRepeatingLesson
  >(values.repeatingLesson || "NO_REPEAT");

  const [disableRepeatingLesson, setDisableRepeatingLesson] = useState<boolean>(
    false,
  );

  const [
    repeatingLessonFrequencyNumber,
    setRepeatingLessonFrequencyNumber,
  ] = useState<AllowedRepeatingLessonFrequencyNumber>(
    values.repeatingLessonFrequencyNumber || null,
  );

  const [test, setTest] = useState(values.test ?? false);

  const updateStartEndDates = () => {
    if (!startDate) {
      handleChange(createFakeEvent("startDateTime", null));
      handleChange(createFakeEvent("endDateTime", null));
      return;
    }

    if (startTime) {
      handleChange(
        createFakeEvent(
          "startDateTime",
          set(startDate, {
            hours: startTime.getHours(),
            minutes: startTime.getMinutes(),
          }),
        ),
      );
    }

    if (endTime) {
      handleChange(
        createFakeEvent(
          "endDateTime",
          set(startDate, {
            hours: endTime.getHours(),
            minutes: endTime.getMinutes(),
          }),
        ),
      );
    }
  };

  useEffect(updateStartEndDates, [startDate, startTime, endTime]);
  const { trackEvent } = useTealium();

  const selectedLearner =
    learners && learners.find(learner => learner.id === values.learnerId);

  const allocation = selectedLearner
    ? selectedLearner.allocations && selectedLearner.allocations[0]
    : undefined;

  const lessonCost = allocation
    ? formatCurrency(
        (allocation.purchaseRate || 0) *
          ((allocation.purchaseVatRate || 0) + 1) || 0,
      )
    : undefined;

  const handleSearchLearnerEvent = () => {
    fireOnce("Calendar|Interaction|Lesson - Search For Learner", () => {
      trackEvent({
        eventCategory: "Calendar",
        eventAction: "Interaction",
        eventLabel: "Lesson - Search For Learner",
      });
    });
  };

  const handleDateEvent = () => {
    fireOnce("Calendar|Interaction|Lesson - Pick A Date", () => {
      trackEvent({
        eventCategory: "Calendar",
        eventAction: "Interaction",
        eventLabel: "Lesson - Pick A Date",
      });
    });
  };

  const handleStartTimeEvent = () => {
    fireOnce("Calendar|Interaction|Lesson - Start Time", () => {
      trackEvent({
        eventCategory: "Calendar",
        eventAction: "Interaction",
        eventLabel: "Lesson - Start Time",
      });
    });
  };

  const handleEndTimeEvent = () => {
    fireOnce("Calendar|Interaction|Lesson - End Time", () => {
      trackEvent({
        eventCategory: "Calendar",
        eventAction: "Interaction",
        eventLabel: "Lesson - End Time",
      });
    });
  };

  const handleTestEvent = () => {
    fireOnce("Calendar|Interaction|Lesson - Test", () => {
      trackEvent({
        eventCategory: "Calendar",
        eventAction: "Interaction",
        eventLabel: "Lesson - Test",
      });
    });
  };
  const handleLearnerChange = (value: Pick<Learner, "id" | "name"> | null) => {
    handleChange(createFakeEvent("learnerId", value?.id));
  };

  useEffect(() => {
    if (selectedLearner) {
      const learnerPropositionId =
        selectedLearner?.allocations?.[0]?.proposition?.id ?? null;
      const getDependedTravelTime = selectedLearner?.allocations?.[0]?.instructor?.travelTime?.find(
        tt => tt.proposition?.id === learnerPropositionId,
      );

      const instructorTravelTime = getDependedTravelTime?.time
        ? (Number(getDependedTravelTime.time) as AllowedTravelTime)
        : 0;
      setTravelTime(instructorTravelTime);
      handleChange(createFakeEvent("travelTime", instructorTravelTime));
    }
  }, [selectedLearner]);

  return (
    <LessonFormFields
      learnersLoading={learnersLoading}
      learners={learners}
      selectedLearner={selectedLearner}
      values={values}
      startDate={startDate}
      handleStartDateChange={val => setStartDate(val)}
      startTime={startTime}
      handleStartTimeChange={(val: Date | null | undefined) => {
        setStartTime(val);
        if (val) {
          setEndTime(addHours(val, 2));
        }
      }}
      endTime={endTime}
      handleEndTimeChange={val => setEndTime(val)}
      test={test}
      handleTestChange={val => {
        setTest(val);
        handleChange(createFakeEvent("test", val));
      }}
      travelTime={travelTime}
      handleTravelTimeChange={option => {
        setTravelTime(Number(option.target.value) as AllowedTravelTime);
        handleChange(
          createFakeEvent("travelTime", Number(option.target.value)),
        );
      }}
      repeatingLesson={repeatingLesson}
      handleRepeatingLessonChange={option => {
        const value = option.target.value as AllowedRepeatingLesson;
        setRepeatingLesson(value);
        handleChange(createFakeEvent("repeatingLesson", value));
        if (value === "NO_REPEAT") {
          setRepeatingLessonFrequencyNumber(null);
          handleChange(createFakeEvent("repeatingLessonFrequencyNumber", null));
        }
      }}
      disableRepeatingLesson={disableRepeatingLesson}
      disableRepeatingLessonChange={val => {
        if (val) {
          setRepeatingLesson("NO_REPEAT");
          handleChange(createFakeEvent("repeatingLesson", "NO_REPEAT"));
          setRepeatingLessonFrequencyNumber(null);
          handleChange(createFakeEvent("repeatingLessonFrequencyNumber", null));
        }
        setDisableRepeatingLesson(val);
      }}
      repeatingLessonFrequencyNumber={repeatingLessonFrequencyNumber}
      handleRepeatingLessonFrequencyNumberChange={option => {
        setRepeatingLessonFrequencyNumber(
          option.target.value as AllowedRepeatingLessonFrequencyNumber,
        );
        handleChange(
          createFakeEvent(
            "repeatingLessonFrequencyNumber",
            option.target.value,
          ),
        );
      }}
      handleLearnerChange={handleLearnerChange}
      lessonCost={lessonCost}
      handleSearchLearnerEvent={handleSearchLearnerEvent}
      handleDateEvent={handleDateEvent}
      handleStartTimeEvent={handleStartTimeEvent}
      handleEndTimeEvent={handleEndTimeEvent}
      handleTestEvent={handleTestEvent}
      handleChange={() => {}}
    />
  );
};

export default StateProvider;
